import React, {useReducer, useMemo, useRef, useEffect, useCallback, createContext} from "react";
import axios from "@common/axios";
import ClassStylesEditor from "./ClassStylesEditor";
import {TextStyles, TextStylesDemo, textStylesRegistry} from "./Styles/BaseStyles";
import {PageHeaderStyles, PageHeaderDemo, pageHeaderStylesRegistry} from "./Styles/PageHeaderStyles";
import {BasicTableStyles, BasicTableDemo, basicTableStylesRegistry} from "./Styles/BasicTableStyles";
import {CustomBlockStyles, CustomBlockDemo, customBlockStylesRegistry} from "./Styles/CustomBlockStyles";
import {SimpleBlockStyles, SimpleBlockDemo, simpleBlockStylesRegistry} from "./Styles/SimpleBlockStyles";
import NavigationRightDevelopment from "../../nav/NavigationRightDevelopment";
import PageHeader from "@common/PageHeader";
import Button from "@common/Button";
import CircularLoader from "@common/CircularLoader";
import {ContentStylesDemoSection, resolveVariant} from "./ContentStylesHelper";
import {generateCropPreview} from "./Styles/PageHeaderImgCrop";
import Alert from "@common/Alert";

const CSS_BASE_CLASS = 'cont-base';
export const EMPTY_CONT_STYLE_PROP = 'empty';

const styleRegistry = Object.freeze({
    pageHeader: {outputSelector: 'ch-page-header', title: 'Header image', editor: PageHeaderStyles, demo: PageHeaderDemo, subRegistry: pageHeaderStylesRegistry},
    textStyles: {outputSelector: CSS_BASE_CLASS, title: 'Text styles', editor: TextStyles, demo: TextStylesDemo, subRegistry: textStylesRegistry},
    basicTable: {outputSelector: 'cont-table-basic', title: 'Table styles', editor: BasicTableStyles, demo: BasicTableDemo, subRegistry: basicTableStylesRegistry},
    customBlocks: {outputSelector: 'cont-block-custom-*', groupName: 'Custom blocks', title: 'Custom block @', totalVariants: 10, editor: CustomBlockStyles, demo: CustomBlockDemo, subRegistry: customBlockStylesRegistry},
    simpleBlocks: {outputSelector: 'cont-block-simple-*', groupName: 'Simple blocks', title: 'Simple block @', totalVariants: 10, editor: SimpleBlockStyles, demo: SimpleBlockDemo, subRegistry: simpleBlockStylesRegistry},
});

export const ClassStylesEditorContext = createContext({});

const buildEditableClasses = () => {
    let groupings = {};
    let lookup = {};

    const processConfig = (configSource, parentCategoryKey = null, inheritedData = {}) => {
        for (const [key, componentData] of Object.entries(configSource)) {
            if (parentCategoryKey == null) {
                if (componentData?.totalVariants > 0) {
                    for (let i = 1; i <= componentData.totalVariants; i++) {
                        const indexedKey = `${key}${i}`;
                        groupings[indexedKey] = {
                            ...resolveVariant(componentData, i),
                            variant: i,
                        };
                        if (componentData.subRegistry) {
                            processConfig(componentData.subRegistry, indexedKey, {
                                ...componentData,
                                variant: i,
                            });
                        }
                    }
                    continue;
                } else {
                    groupings[key] = componentData;
                }
            }

            const currentInheritance = {
                editor: componentData.editor || inheritedData.editor,
                demo: componentData.demo || inheritedData.demo,
                ...(inheritedData?.variant && {variant: inheritedData.variant}),
            };

            if (componentData.subRegistry && parentCategoryKey === null) {
                processConfig(componentData.subRegistry, key, currentInheritance);
                continue;
            }

            if (parentCategoryKey) {
                const buildVariant = (variantIndex) => {
                    const resolvedData = resolveVariant(componentData, variantIndex);

                    return {
                        ...currentInheritance,
                        ...resolvedData,
                        parentClassKey: parentCategoryKey,
                        variant: variantIndex
                    };
                };

                if (inheritedData.variant && key.includes('*')) {
                    const variantClassKey = resolveVariant(key, inheritedData.variant);
                    lookup[variantClassKey] = buildVariant(inheritedData.variant);
                } else if (componentData?.totalVariants > 0) {
                    for (let i = 1; i <= componentData.totalVariants; i++) {
                        const variantClassKey = resolveVariant(key, i);
                        lookup[variantClassKey] = buildVariant(i);
                    }
                } else {
                    lookup[key] = {
                        ...currentInheritance,
                        ...componentData,
                        parentClassKey: parentCategoryKey,
                    };
                }
            }
        }
    };

    processConfig(styleRegistry);

    return {lookup, groupings};
};

const {lookup: EDITABLE_CLASS_LOOKUP, groupings: SIDEBAR_GROUPS} = buildEditableClasses();

const ACTIONS = {
    HYDRATE_STATE: 'hydrateState',
    GENERIC_VALUE_UPDATE: 'genericValueUpdate',
    UPDATE_HEADER_PREVIEW: 'updateHeaderPreview',
    UPDATE_CLASS_STYLES: 'updateClassStyles',
    UPDATE_SAVE_STATUS: 'updateSaveStatus',
}
const SAVE_STATES = {
    UNSAVED: 'unsaved',
    SAVING: 'saving',
    UNCHANGED: 'unchanged'
}
const defaultState = {
    headerImage: {},
    styles: {},
    edit: {},
    workspace: null,
    saveStatus: SAVE_STATES.UNCHANGED,
    loading: true
}

const reducer = (state, action) => {
    if (action.type === ACTIONS.HYDRATE_STATE) {
        const defaultCategory = Object.keys(styleRegistry)[0] || null;
        return {
            ...state,
            headerImage: action.payload.headerImage,
            styles: action.payload.styles,
            edit: {class: defaultCategory},
            loading: action.payload.loading
        }
    } else if (action.type === ACTIONS.GENERIC_VALUE_UPDATE) {
        const newState = {...state};
        for (const key in action.payload.newValues) {
            if (Object.prototype.hasOwnProperty.call(state, key)) {
                const value = action.payload.newValues[key];
                if (typeof value === 'function') {
                    newState[key] = value(state[key]);
                } else {
                    newState[key] = value;
                }
            } else {
                console.warn(`Warning: Attempted to update a non-existent state property: "${key}"`);
            }
        }
        return newState;
    } else if (action.type === ACTIONS.UPDATE_HEADER_PREVIEW) {
        return {...state, headerImage: {...state.headerImage, previewImage: action.payload.previewImage}}
    } else if (action.type === ACTIONS.UPDATE_CLASS_STYLES) {
        let storageKey = '';
        const parentClassKey = action.payload.className.replace(/\d+$/, "");
        if (action.payload.className !== parentClassKey) {
            const variant = action.payload.className.slice(parentClassKey.length);
            const registryEntry = styleRegistry[parentClassKey];
            if (registryEntry) {
                storageKey = resolveVariant(styleRegistry[parentClassKey]?.outputSelector, variant);
            }
        } else {
            storageKey = styleRegistry[parentClassKey]?.outputSelector;
        }

        if (!storageKey) {
            storageKey = CSS_BASE_CLASS;
        }

        const updatedClassNewStyles = {
            ...(state.styles[storageKey] || {}),
            ...action.payload.newStyles
        }

        const filteredStyles = Object.keys(updatedClassNewStyles).reduce((acc, style) => {
            if (updatedClassNewStyles[style] !== EMPTY_CONT_STYLE_PROP) {
                acc[style] = updatedClassNewStyles[style];
            }
            return acc;
        }, {});

        return {
            ...state,
            styles: {
                ...state.styles,
                [storageKey]: filteredStyles
            }
        };
    } else if (action.type === ACTIONS.UPDATE_SAVE_STATUS) {
        return {...state, saveStatus: action.payload}
    } else {
        return state;
    }
}

const contentStylesEditor = props => {
    const [state, dispatch] = useReducer(reducer, defaultState);
    const workspaceRef = useRef();

    useEffect(() => {
        const contentStyleSheet = document.getElementById('syllabusContentStyleSheet');
        if (contentStyleSheet) contentStyleSheet.disabled = true;

        initLoad();

        return () => {
            if (contentStyleSheet) contentStyleSheet.removeAttribute('disabled');
            updateEditClass(null);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        generateCropPreview(state.headerImage).then(preview => {
            if (!controller.signal.aborted && preview) {
                dispatch({type: ACTIONS.UPDATE_HEADER_PREVIEW, payload: {previewImage: preview}});
            }
        });

        return () => controller.abort();
    }, [state.headerImage.crop]);

    const initLoad = () => {
        axios.get('/web/ui/contentStyles/getStyles')
            .then(response => {
                const headerImage = response.data.headerImage;
                const contentStyles = response.data.contentStyles;
                dispatch({
                    type: ACTIONS.HYDRATE_STATE,
                    payload: {headerImage: headerImage, styles: contentStyles, loading: false}
                });
            });
    }

    const handleDemoComponentHover = (target, classKey) => {
        const componentData = EDITABLE_CLASS_LOOKUP[classKey];
        if (!componentData) return;

        target.classList.add("ch-s_ed-component-hover");
        target.setAttribute('data-name', componentData.title);
    }

    const handleDemoComponentLeave = target => {
        target.classList.remove("ch-s_ed-component-hover");
        target.removeAttribute('data-name');
    }

    const handleDemoComponentClick = (classKey) => {
        const componentData = EDITABLE_CLASS_LOOKUP[classKey];
        if (!componentData) return;

        let targetClass = classKey;
        let targetCategory = componentData.parentClassKey;

        if (componentData.slave) {
            const variantNum = componentData.variant;
            const linkedClass = resolveVariant(componentData.slave, variantNum);

            if (EDITABLE_CLASS_LOOKUP[linkedClass]) {
                targetClass = linkedClass;
                targetCategory = EDITABLE_CLASS_LOOKUP[linkedClass].parentClassKey;
            }
        }

        updateEditClass(targetCategory, targetClass);
    }

    const updateEditClass = useCallback((className, highlightClassName = null) => {
        dispatch({
            type: ACTIONS.GENERIC_VALUE_UPDATE,
            payload: {
                newValues: {
                    edit: {
                        class: className,
                        highlightClass: highlightClassName,
                        highlightTimestamp: Date.now(),
                    }
                }
            }
        });
    }, []);

    const dynamicCSS = useMemo(() => {
        if (!state.styles) return "";
        let css = "";

        Object.entries(state.styles).forEach(([key, styleProps]) => {
            const componentData = EDITABLE_CLASS_LOOKUP[key] || Object.values(EDITABLE_CLASS_LOOKUP).find(v => v.parentClassKey === key);
            let selectorName = key;

            if (componentData) {
                const parentClassKey = componentData.parentClassKey.replace(/\d+$/, "");
                if (componentData.parentClassKey !== parentClassKey) {
                    const variant = componentData.parentClassKey.slice(parentClassKey.length);
                    selectorName = resolveVariant(styleRegistry[parentClassKey]?.outputSelector, variant);
                } else {
                    selectorName = styleRegistry[parentClassKey]?.outputSelector;
                }
            }

            const rules = Object.entries(styleProps)
                .map(([propName, value]) => {
                    const cleanProp = propName.startsWith('--') ? propName : `--${propName}`;
                    const cssValue = Array.isArray(value) ? value.join(',') : value;
                    return `  ${cleanProp}: ${cssValue} !important;`;
                })
                .join('\n');

            const selector = (selectorName === CSS_BASE_CLASS || !selectorName?.startsWith('cont-'))
                ? `.${CSS_BASE_CLASS}`
                : `.${CSS_BASE_CLASS} .${selectorName}`;

            css += `${selector} {\n${rules}\n}\n`;
        });

        return css;
    }, [state.styles]);

    const updateStyles = useCallback((newStyles, isFinal = true) => {
        if (workspaceRef.current) {
            Object.entries(newStyles).forEach(([prop, val]) => {
                const cssVar = prop.startsWith('--') ? prop : `--${prop}`;
                const cssVal = Array.isArray(val) ? val.join(',') : val;

                workspaceRef.current.style.setProperty(cssVar, cssVal);
            });
        }

        if (isFinal) {
            dispatch({type: ACTIONS.UPDATE_CLASS_STYLES, payload: {className: state.edit.class, newStyles: newStyles}});
            dispatch({type: ACTIONS.UPDATE_SAVE_STATUS, payload: SAVE_STATES.UNSAVED});
        }
    }, [state.edit.class]);

    const updateWorkspace = useCallback(workspaceObj => {
        dispatch({type: ACTIONS.GENERIC_VALUE_UPDATE, payload: {newValues: {workspace: workspaceObj}}});
    }, []);

    const updateCallback = useCallback((newValues, newChanges = false) => {
        dispatch({type: ACTIONS.GENERIC_VALUE_UPDATE, payload: {newValues: newValues}});
        if (newChanges) dispatch({type: ACTIONS.UPDATE_SAVE_STATUS, payload: SAVE_STATES.UNSAVED});
    }, []);

    const saveStyles = () => {
        let data = {
            contentStyles: state.styles
        };
        if (state.headerImage.previewImage) {
            let cleanHeaderImage = { ...state.headerImage };
            delete cleanHeaderImage.previewImage;
            delete cleanHeaderImage.masterUri;
            data.headerImage = cleanHeaderImage;
        }
        const cachedSaveStatus = state.saveStatus;
        dispatch({type: ACTIONS.UPDATE_SAVE_STATUS, payload: SAVE_STATES.SAVING});
        axios.post('/web/ui/contentStyles/updateStyles', data, {headers: {'Content-Type': 'application/json'}})
            .then(response => {
                displayToastMessage('Saved', 'The styles have been saved', 'success');
                let headerImage = response.data.headerImage;
                const contentStyles = response.data.contentStyles;
                dispatch({
                    type: ACTIONS.GENERIC_VALUE_UPDATE,
                    payload: {
                        newValues: {
                            headerImage: headerImage,
                            styles: contentStyles,
                            saveStatus: SAVE_STATES.UNCHANGED
                        }
                    }});
            })
            .catch(error => {
                dispatch({type: ACTIONS.UPDATE_SAVE_STATUS, payload: cachedSaveStatus});
            })
    }

    const publishStyles = () => {
        axios.get('/web/ui/contentStyles/publishStyles')
            .then(response => {
                displayToastMessage('Published', 'The styles have been published', 'success');
            });
    }

    const downloadStyles = () => {
        axios.get('/web/ui/contentStyles/downloadStyles', {}, {responseType: 'blob'})
            .then(response => {
                const contentDisposition = response.headers['content-disposition'];
                const filename = contentDisposition ? contentDisposition.split('filename=')[1].replace(/"/g, '') : 'contentStyles.css';
                const fileURL = window.URL.createObjectURL(new Blob([response.data], { type: 'text/css' }));
                const fileLink = document.createElement('a');
                fileLink.href = fileURL;
                fileLink.setAttribute('download', filename);
                document.body.appendChild(fileLink);
                fileLink.click();

                document.body.removeChild(fileLink);
            })
            .catch(error => {
                console.error('Error downloading the file', error);
            });
    }

    return (
        <div className="ch-s_ed">
            {state.loading && (
                <div>
                    <CircularLoader />
                </div>
            )}
            <ClassStylesEditorContext.Provider value={{
                headerImage: state.headerImage,
                styles: state.styles,
                workspace: state.workspace,
                handleDemoComponentHover,
                handleDemoComponentLeave,
                handleDemoComponentClick,
            }}>
                <div className="flexRow margin-t--l" style={{display: (state.loading ? "none" : "flex")}}>
                    <div className="flexColumn" style={{position: "relative", width: '100%'}}>
                        <PageHeader
                            title="Theme editor"
                            buttons={!state.workspace ? (
                                    <>
                                        {state.saveStatus === SAVE_STATES.SAVING && (
                                            <div className="flex" style={{alignSelf: 'center'}}>
                                                <CircularLoader coreOnly={true} />
                                            </div>
                                        )}
                                        <Button
                                            type={state.saveStatus === SAVE_STATES.UNSAVED ? "solid" : "outline"}
                                            disabled={state.saveStatus === SAVE_STATES.SAVING}
                                            onClick={saveStyles}
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            type="outline"
                                            disabled={state.saveStatus === SAVE_STATES.SAVING}
                                            onClick={publishStyles}
                                        >
                                            Publish
                                        </Button>
                                        <Button
                                            type="outline"
                                            disabled={state.saveStatus === SAVE_STATES.SAVING}
                                            onClick={downloadStyles}
                                        >
                                            Download CSS
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        {state?.workspace?.buttons}
                                        <Button type="outline" onClick={() => updateWorkspace(null)}>Cancel</Button>
                                    </>
                                )
                            }
                            sticky={true}
                        />
                        <div style={{display: state.workspace ? 'none' : 'block'}}>
                            <div
                                ref={workspaceRef}
                                className={CSS_BASE_CLASS}
                            >
                                <style>
                                    {dynamicCSS}
                                </style>
                                <div className="ch-s_ed-demo">
                                    <Alert
                                        type={"info"}
                                        closable
                                        title={
                                            <>Editor help</>
                                        }
                                        description={
                                            <div>
                                                <div className="flexColumn mediumGap">
                                                    <div>
                                                        <strong>Save:</strong> Updates the database and image links.
                                                        <span className="flex ch-text-small text-muted">
                                                            Note: Images are cached; you may need to re-login to see changes in the editor.
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <strong>Publish:</strong> Generates a new CSS file and updates the live site.
                                                    </div>
                                                    <hr />
                                                    <div>
                                                        Click any element in the preview to jump to its settings.
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    />
                                    {Object.entries(styleRegistry).map(([key, componentData]) => {
                                        const demoProps = {
                                            ...(key === "pageHeader" && {headerImage: state.headerImage}),
                                            ...(componentData?.totalVariants > 1 && {totalVariants: componentData.totalVariants}),
                                        }
                                        return (
                                            <ContentStylesDemoSection
                                                key={key}
                                                id={`preview-section-${key}`}
                                                data-classname={key}
                                                title={componentData?.groupName ?? componentData.title}
                                            >
                                                <componentData.demo
                                                    {...demoProps}
                                                    demoEvents={(targetKey) => ({
                                                        onMouseEnter: (e) => {
                                                            handleDemoComponentHover(e.currentTarget, targetKey);
                                                        },
                                                        onMouseLeave: (e) => {
                                                            handleDemoComponentLeave(e.currentTarget);
                                                        },
                                                        onClick: (e) => {
                                                            e.stopPropagation();
                                                            handleDemoComponentClick(targetKey);
                                                        },
                                                    })}
                                                />
                                            </ContentStylesDemoSection>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        {state?.workspace && (
                            <div className="flex ch-s_ed-workspace">
                                {state.workspace.output}
                            </div>
                        )}
                    </div>
                    <NavigationRightDevelopment>
                        <ClassStylesEditor
                            state={state}
                            categories={SIDEBAR_GROUPS}
                            updateEditClass={updateEditClass}
                            updateStylesCallback={updateStyles}
                            updateWorkspace={updateWorkspace}
                            updateCallback={updateCallback}
                        />
                    </NavigationRightDevelopment>
                </div>
            </ClassStylesEditorContext.Provider>
        </div>
    )
}

export default contentStylesEditor;